/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { useForm } from '@/account/hooks/components/useForm';
import { FormProps, FormRef } from '@/account/types';
import FormInputs from '@/account/components/Form/FormInputs';
import { forwardRef, memo, useImperativeHandle } from 'preact/compat';
import '@/account/components/Form/Form.css';
import { classes, Slot } from '@adobe-commerce/elsie/lib';
import normalizeGetAddressData from '@/account/lib/normalizeGetAddressData';
import getFormValuesFromRef from '@/account/lib/getFormValuesFromRef';
import { AddressFormLoader } from '../AccountLoaders';
import { Checkbox } from '@adobe-commerce/elsie/components';
import { useText } from '@adobe-commerce/elsie/i18n';

export const Form = memo(
  forwardRef<FormRef, FormProps>(
    ({
      setInputChange,
      showFormLoader,
      slots,
      name,
      loading,
      children,
      className = 'defaultForm',
      fieldsConfig,
      onSubmit,
      onChange,
      forwardFormRef,
      regionOptions,
      showSaveCheckBox,
      handleSaveCheckBoxAddress,
      saveCheckBoxAddress,
      fieldIdPrefix,
    }) => {
      const translations = useText({
        saveAddressBook: 'Account.AddressForm.formText.saveAddressBook',
      });

      const {
        isDataValid,
        formData,
        errors,
        formRef,
        handleInputChange,
        handleBlur,
        handleSubmit,
        handleValidationSubmit,
        onFocus,
      } = useForm({
        fieldsConfig,
        onSubmit,
        onChange,
        setInputChange,
        regionOptions,
        formName: name!,
      });

      useImperativeHandle(forwardFormRef, () => {
        const modifyFormRef = getFormValuesFromRef(formRef);

        return {
          handleValidationSubmit,
          requestSubmit: () => {
            formRef.current?.requestSubmit();
          },
          formData: normalizeGetAddressData(modifyFormRef, true),
          isDataValid,
        };
      });

      if (showFormLoader || !fieldsConfig?.length) return <AddressFormLoader />;

      return (
        <form
          className={classes(['account-form', className])}
          onSubmit={handleSubmit}
          name={name}
          ref={formRef}
        >
          <FormInputs
            className={className}
            loading={loading}
            fields={fieldsConfig}
            onChange={handleInputChange}
            onBlur={handleBlur}
            errors={errors}
            values={formData}
            onFocus={onFocus}
            slots={slots}
            fieldIdPrefix={fieldIdPrefix}
          />
          {slots?.AddressFormInputs ? (
            <Slot
              data-testid="addressFormInputs"
              name="AddressFormInputs"
              slot={slots.AddressFormInputs}
              context={{ formActions: { handleChange: handleInputChange } }}
            />
          ) : null}
          {showSaveCheckBox ? (
            <div className={'account-address-form--saveAddressBook'}>
              <Checkbox
                data-testid="testSaveAddressBook"
                name="saveAddressBook"
                label={translations.saveAddressBook}
                // @ts-ignore
                checked={saveCheckBoxAddress}
                onChange={(event) => {
                  handleInputChange(event);
                  handleSaveCheckBoxAddress?.(event);
                }}
              />
            </div>
          ) : null}
          {children}
        </form>
      );
    }
  )
);
